/**
 * Expenses module — shared costs between co-parents. List with who-paid, split
 * (50/50 or custom payer %), settle action, and an outstanding-total summary.
 */
import { useMemo, useState } from 'react';
import type { Expense } from '@solandia/core';
import {
  createExpenseInputSchema,
  formatMoney,
  splitAmount,
  toMinorUnits,
} from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { makePeopleResolver } from '../../family/people';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

export function ExpensesModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, me, children } = useFamily();
  // In family mode the 50/50 split machinery is hidden: we show a simple
  // "kto zaplatil" chip and a month total instead of split shares + settle.
  // The split data (splitPct, settledAt) stays on the model, just unsurfaced.
  const separated = family.mode === 'separated';

  const { data, loading, error, reload } = useAsync<Expense[]>(async () => {
    const res = await api.listExpenses(family.id);
    return res.items;
  }, [family.id]);

  const people = useMemo(
    () => makePeopleResolver(me, t.expenses.you),
    [me, t.expenses.you],
  );

  const ordered = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.incurredOn.localeCompare(a.incurredOn)),
    [data],
  );

  const outstanding = useMemo(() => {
    let totalMinor = 0;
    let currency = 'EUR';
    for (const e of ordered) {
      if (!e.settledAt) {
        totalMinor += e.amountMinor;
        currency = e.currency;
      }
    }
    return { totalMinor, currency };
  }, [ordered]);

  // Family mode: total spent in the current calendar month (no settlement
  // concept). Computed over all expenses regardless of split/settle state.
  const monthTotal = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let totalMinor = 0;
    let currency = 'EUR';
    for (const e of ordered) {
      const d = new Date(e.incurredOn);
      if (d.getFullYear() === y && d.getMonth() === m) {
        totalMinor += e.amountMinor;
        currency = e.currency;
      }
    }
    return { totalMinor, currency };
  }, [ordered]);

  const childName = (id: string | null) =>
    id ? children.find((c) => c.id === id)?.name ?? t.expenses.childAll : t.expenses.childAll;

  const [adding, setAdding] = useState(false);

  async function settle(id: string) {
    await api.settleExpense(family.id, id);
    reload();
  }
  async function remove(id: string) {
    if (!window.confirm(t.common.confirmDelete)) return;
    await api.deleteExpense(family.id, id);
    reload();
  }

  return (
    <section>
      <SectionHeader
        title={separated ? t.expenses.titleSeparated : t.expenses.titleFamily}
        subtitle={separated ? t.expenses.leadSeparated : t.expenses.lead}
        action={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            ＋ {t.expenses.add}
          </button>
        }
      />

      {ordered.length > 0 ? (
        <div className="banner banner-info" style={{ marginBottom: 16 }}>
          <span aria-hidden="true">💶</span>
          <div className="grow">
            {separated ? (
              <>
                {t.expenses.totalOutstanding}:{' '}
                <strong>{formatMoney(outstanding.totalMinor, outstanding.currency)}</strong>
              </>
            ) : (
              <>
                {t.expenses.totalMonth}:{' '}
                <strong>{formatMoney(monthTotal.totalMinor, monthTotal.currency)}</strong>
              </>
            )}
          </div>
        </div>
      ) : null}

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
      {ordered.length === 0 && !loading ? (
        <EmptyState emoji="🧾" title={t.expenses.empty} />
      ) : null}

      <ul className="list">
        {ordered.map((e) => {
          const split = splitAmount(e.amountMinor, e.splitPct);
          const payerIsMe = people.isMe(e.payerId);
          return (
            <li key={e.id} className="list-item">
              <div className="grow stack-sm">
                <div className="row wrap">
                  <strong>{formatMoney(e.amountMinor, e.currency)}</strong>
                  <Badge>{e.category}</Badge>
                  {separated ? (
                    e.settledAt ? (
                      <Badge tone="success">{t.expenses.settled}</Badge>
                    ) : (
                      <Badge tone="danger">{t.expenses.outstanding}</Badge>
                    )
                  ) : null}
                </div>
                {e.description ? <span className="text-sm">{e.description}</span> : null}
                <span className="text-sm muted">
                  {childName(e.childId)} · {new Date(e.incurredOn).toLocaleDateString('sk-SK')}
                </span>
                {separated ? (
                  <span className="text-xs muted">
                    {t.expenses.paidBy}: {people.label(e.payerId)} ·{' '}
                    {e.splitPct === 50
                      ? t.expenses.split5050
                      : `${e.splitPct}/${100 - e.splitPct}`}{' '}
                    ({payerIsMe ? t.expenses.payerShare : t.expenses.otherShare}{' '}
                    {formatMoney(split.payerShare, e.currency)})
                  </span>
                ) : (
                  <span className="row wrap">
                    <Badge tone="primary">
                      {t.expenses.paidByFamily}: {people.label(e.payerId)}
                    </Badge>
                  </span>
                )}
              </div>
              <div className="stack-sm" style={{ alignItems: 'flex-end' }}>
                {separated && !e.settledAt ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => settle(e.id)}
                  >
                    {t.expenses.settle}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => remove(e.id)}
                  aria-label={t.common.delete}
                >
                  {t.common.delete}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {adding ? (
        <AddExpenseDialog
          separated={separated}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function AddExpenseDialog({
  separated,
  onClose,
  onCreated,
}: {
  separated: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, children } = useFamily();
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Škola');
  const [description, setDescription] = useState('');
  const [childId, setChildId] = useState<string>('');
  const [incurredOn, setIncurredOn] = useState(new Date().toISOString().slice(0, 10));
  const [splitMode, setSplitMode] = useState<'5050' | 'custom'>('5050');
  const [splitPct, setSplitPct] = useState(50);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const major = Number(amount.replace(',', '.'));
    if (!Number.isFinite(major) || major <= 0) {
      setErrors({ amount: t.common.required });
      return;
    }
    const input = {
      childId: childId || null,
      amountMinor: toMinorUnits(major),
      currency: 'EUR',
      category: category.trim(),
      description: description.trim() || null,
      incurredOn,
      splitPct: splitMode === '5050' ? 50 : splitPct,
    };
    const parsed = createExpenseInputSchema.safeParse(input);
    if (!parsed.success) {
      setErrors({ amount: t.errors.generic });
      return;
    }
    setBusy(true);
    try {
      await api.createExpense(family.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ amount: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.expenses.add} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <div className="form-grid form-grid-2">
          <Field label={`${t.expenses.amountLabel} (€)`} error={errors.amount}>
            {({ id, invalid }) => (
              <input
                id={id}
                className="input"
                inputMode="decimal"
                value={amount}
                aria-invalid={invalid}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
                autoFocus
              />
            )}
          </Field>
          <Field label={t.expenses.incurredOnLabel}>
            {({ id }) => (
              <input
                id={id}
                type="date"
                className="input"
                value={incurredOn}
                onChange={(e) => setIncurredOn(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={t.expenses.categoryLabel}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t.expenses.descriptionLabel} (${t.common.optional})`}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label={t.expenses.childLabel}>
          {({ id }) => (
            <select
              id={id}
              className="select"
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
            >
              <option value="">{t.expenses.childAll}</option>
              {children.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.avatarEmoji} {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {separated ? (
          <Field
            label={t.expenses.splitLabel}
            hint={
              splitMode === 'custom'
                ? `${t.expenses.payerShare}: ${splitPct}% · ${t.expenses.otherShare}: ${100 - splitPct}%`
                : undefined
            }
          >
            {({ id }) => (
              <div className="stack-sm">
                <select
                  id={id}
                  className="select"
                  value={splitMode}
                  onChange={(e) => setSplitMode(e.target.value as '5050' | 'custom')}
                >
                  <option value="5050">{t.expenses.split5050}</option>
                  <option value="custom">{t.expenses.splitCustom}</option>
                </select>
                {splitMode === 'custom' ? (
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={splitPct}
                    onChange={(e) => setSplitPct(Number(e.target.value))}
                    aria-label={t.expenses.payerShare}
                  />
                ) : null}
              </div>
            )}
          </Field>
        ) : null}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
